import { apiClient } from '@/auth';

/**
 * Full meal-plan API surface, mirroring v1's `mealPlanApi`. Schemas verified
 * against MNT swagger:21574-21807 — every response shape and request body
 * comes from there. Phase 1 of the meal-plan parity work; phase 2 will build
 * the list / intake / viewer UI on top of these primitives.
 */

/* -------------------------------------------------------------------------- */
/*                                 ENUMS                                      */
/* -------------------------------------------------------------------------- */

/** Plan lifecycle. The viewer auto-triggers `generate` on `draft`, polls
 *  every 3s while `generating`, surfaces grocery / PDF / email actions on
 *  `generated`, and locks editing on `finalized`. */
export type MealPlanStatus = 'draft' | 'generating' | 'generated' | 'finalized';

/** Activity level enum used by the TDEE calculator (swagger.yaml:21773). */
export type ActivityLevel =
  | 'sedentary'
  | 'lightly_active'
  | 'moderately_active'
  | 'very_active'
  | 'extra_active';

/** Sex enum the TDEE endpoint accepts. v1 maps `mtf_female` → `female` and
 *  `ftm_male` → `male` before posting (clinician/src/pages/patient-meal-plan-intake.tsx:91). */
export type TdeeSex = 'male' | 'female';

/** Per-meal toggles used to scope generation. v1's intake form exposes the
 *  first four; the server stores them as a flat object so we keep it loose. */
export type ActiveSlots = {
  breakfast?: boolean;
  lunch?: boolean;
  dinner?: boolean;
  snacks?: boolean;
} & Record<string, boolean | undefined>;

/* -------------------------------------------------------------------------- */
/*                            RESPONSE / REQUEST                              */
/* -------------------------------------------------------------------------- */

/**
 * Slimmer of the two list-response shapes — what `GET /patients/{id}/meal-plans`
 * returns. Per swagger:21591. Skips per-macro detail and the days[] tree;
 * use `MealPlanDetailResponse` for the viewer.
 */
export type MealPlanSummary = {
  id: number;
  patient_id: number;
  clinician_user_id: number;
  clinic_id?: number;
  title: string;
  status: MealPlanStatus | string;
  num_days: number;
  target_calories?: number | null;
  active_slots?: ActiveSlots | null;
  family_size?: number | null;
  motivations?: string | null;
  created_time: string;
  updated_time?: string;
};

/**
 * Single-plan response from `POST /patients/{id}/meal-plans` and `.../duplicate`.
 * No `days[]` tree — that only comes back from the viewer GET (`MealPlanDetail`).
 * Per swagger:21608.
 */
export type MealPlanResponse = MealPlanSummary & {
  target_protein_g?: number | null;
  target_carbs_g?: number | null;
  target_fat_g?: number | null;
  diet_labels?: string[] | null;
  health_labels?: string[] | null;
  exclusions?: string[] | null;
  intake_form_data?: IntakeFormData | null;
};

/**
 * Detail response from `GET /patients/{id}/meal-plans/{plan_id}`. Carries
 * the full days/slots tree the viewer renders. Per swagger:21632.
 */
export type MealPlanDetail = MealPlanResponse & {
  ai_insight?: string | null;
  grocery_list?: Record<string, unknown> | null;
  days?: MealPlanDay[];
};

export type MealPlanDay = {
  id: number;
  day_number: number;
  daily_summary?: DailySummary | null;
  slots?: MealPlanSlot[];
};

/**
 * A slot is one meal-of-the-day for one day. `recipe_data` is the
 * Edamam-shaped payload v1 reads as `RecipeData`
 * (clinician/src/pages/patient-meal-plan-viewer.tsx:54-66) — kept loose
 * here since the server has many similar variants over time. The viewer
 * narrows it at render time.
 */
export type MealPlanSlot = {
  id: number;
  slot_type: string; // 'breakfast' | 'lunch' | 'dinner' | 'snacks' | 'snack' | 'other'
  sort_order: number;
  recipe_data?: SlotRecipeData | null;
};

/** Subset of fields v1's viewer reads off `recipe_data`. The server hands
 *  back the whole Edamam payload plus its own `clinician_notes`; we only
 *  type the keys we read so old/new server shapes both decode. */
export type SlotRecipeData = {
  link?: string;
  recipe?: {
    label?: string;
    /** Legacy single image URL. Newer Edamam payloads omit this in favor of
     *  the sized variants under `images`; use `bestRecipeImage()` (below)
     *  to pick whichever is available. */
    image?: string;
    /** Sized variants. Edamam ships THUMBNAIL / SMALL / REGULAR / LARGE
     *  with each variant pointing to its own URL — we prefer REGULAR for
     *  card thumbnails, falling through to whichever exists. */
    images?: Partial<
      Record<'THUMBNAIL' | 'SMALL' | 'REGULAR' | 'LARGE', { url?: string }>
    >;
    /** Edamam's API endpoint URL (returns JSON, not HTML). Last-resort for
     *  the recipe-page link. */
    url?: string;
    /** Edamam's human-readable HTML recipe page — what clinicians actually
     *  want when they click "Full recipe". Always prefer this. */
    shareAs?: string;
    source?: string;
    ingredientLines?: string[];
    yield?: number;
    calories?: number;
    totalNutrients?: Record<string, { quantity: number; unit: string }>;
  };
  clinician_notes?: string | null;
} & Record<string, unknown>;

/**
 * Resolve the best recipe image URL out of the various shapes MNT / Edamam
 * have returned over time. Tries (in order):
 *   1. `recipe.image` — the legacy single URL
 *   2. `recipe.images.REGULAR.url` — current default size on new payloads
 *   3. `recipe.images.LARGE.url`
 *   4. `recipe.images.SMALL.url`
 *   5. `recipe.images.THUMBNAIL.url`
 *
 * Returns null when none of them yield a usable string so the caller can
 * fall through to a placeholder.
 */
/**
 * Resolve the best "open in browser" URL for a recipe. Priority:
 *   1. `recipe.shareAs` — Edamam's public HTML recipe page (the right
 *      surface for a "Full recipe" link).
 *   2. `recipe.url` — `data.edamam.com/recipes/...`, often redirects to
 *      the public page but not guaranteed.
 *   3. `slotData.link` — top-level link on the saved slot. Many MNT
 *      records stash Edamam's API endpoint here (`api.edamam.com/api/...`),
 *      which returns JSON, not HTML — so it goes last.
 *
 * Returns null when no usable link exists; caller hides the button in
 * that case.
 */
export const bestRecipeLink = (
  data: SlotRecipeData | undefined | null,
): string | null => {
  if (!data) return null;
  const recipe = data.recipe;
  if (recipe?.shareAs && typeof recipe.shareAs === 'string') return recipe.shareAs;
  if (recipe?.url && typeof recipe.url === 'string') {
    // `api.edamam.com/...` returns JSON in the browser — skip and fall
    // through to data.link instead. data.edamam.com is the redirect host.
    if (!recipe.url.includes('api.edamam.com')) return recipe.url;
  }
  if (typeof data.link === 'string' && data.link && !data.link.includes('api.edamam.com')) {
    return data.link;
  }
  return null;
};

export const bestRecipeImage = (
  recipe: SlotRecipeData['recipe'] | undefined | null,
): string | null => {
  if (!recipe) return null;
  if (typeof recipe.image === 'string' && recipe.image) return recipe.image;
  const sized = recipe.images;
  if (sized) {
    for (const key of ['REGULAR', 'LARGE', 'SMALL', 'THUMBNAIL'] as const) {
      const url = sized[key]?.url;
      if (typeof url === 'string' && url) return url;
    }
  }
  return null;
};

/** Daily-summary numeric breakdown — what the viewer renders as macro
 *  progress bars under the day tab. Most fields are optional because
 *  partial-day plans (e.g. snacks-only) fill some and not others. */
export type DailySummary = {
  calories?: number;
  protein_g?: number;
  carbs_g?: number;
  fat_g?: number;
  fiber_g?: number;
  sugar_g?: number;
  sodium_mg?: number;
  cholesterol_mg?: number;
  saturated_fat_g?: number;
  potassium_mg?: number;
  calcium_mg?: number;
  iron_mg?: number;
  vitamin_c_mg?: number;
  vitamin_a_mcg?: number;
} & Record<string, number | undefined>;

/**
 * Intake form data echoed back on the plan response. v1 stores both
 * unit systems in this object — `unit_system: 'metric' | 'imperial'`
 * decides which set of fields are populated. Round-tripped verbatim
 * so we don't lose any clinic-side fields the server adds later.
 */
export type IntakeFormData = {
  age_years?: number;
  sex?: TdeeSex;
  activity_level?: ActivityLevel;
  bmr_calories?: number;
  tdee_calories?: number;
  unit_system?: 'metric' | 'imperial';
  weight_kg?: number;
  height_cm?: number;
  weight_lb?: number;
  height_ft?: number;
  height_in?: number;
} & Record<string, unknown>;

/* -------------------------------------------------------------------------- */
/*                       CREATE / SWAP / GROCERY / NOTE                       */
/* -------------------------------------------------------------------------- */

/**
 * Body for `POST /patients/{id}/meal-plans`. Per swagger:21574.
 * Title and num_days are required; everything else is optional and the
 * server fills sensible defaults.
 */
export type CreateMealPlanInput = {
  title: string;
  num_days: number;
  target_calories?: number | null;
  target_protein_g?: number | null;
  target_carbs_g?: number | null;
  target_fat_g?: number | null;
  diet_labels?: string[] | null;
  health_labels?: string[] | null;
  exclusions?: string[] | null;
  intake_form_data?: IntakeFormData | null;
  active_slots?: ActiveSlots | null;
  family_size?: number | null;
  motivations?: string | null;
};

/** TDEE calculator request. Server contract is metric-only (kg/cm) per
 *  swagger:21766 — v1 converts imperial inputs (lb/ft/in) client-side
 *  before posting. */
export type TdeeCalculateInput = {
  age_years: number;
  weight_kg: number;
  height_cm: number;
  sex: TdeeSex;
  activity_level: ActivityLevel;
};

export type TdeeCalculateResponse = {
  tdee_calories: number;
  bmr_calories: number;
  macros_suggestion?: {
    protein_g: number;
    carbs_g: number;
    fat_g: number;
  } | null;
};

export type SwapSuggestionsInput = {
  slot_id: number;
  keywords: string;
};

export type SwapSuggestionsResponse = {
  /** Each suggestion is the same Edamam-shaped payload as a slot's
   *  `recipe_data` — v1 casts it directly. */
  suggestions: SlotRecipeData[];
};

/**
 * Body for `POST /patients/{id}/meal-plans/{id}/swap-confirm`. v1 always
 * sends `expected_recipe_label` so the server can detect a stale-suggestions
 * race (another tab issued swap-suggestions in parallel and overwrote the
 * cache).
 */
export type SwapConfirmInput = {
  slot_id: number;
  suggestion_index: number;
  expected_recipe_label?: string | null;
};

export type SwapRecipeResponse = {
  slot: MealPlanSlot;
  daily_summary?: DailySummary | null;
};

export type SwapIngredientInput = {
  slot_id: number;
  ingredient_index: number;
  keyword: string;
};

export type SwapIngredientResponse = {
  suggestions: Array<{
    food_name?: string;
    ingredient_line?: string;
  }>;
};

export type ConfirmIngredientSwapInput = {
  slot_id: number;
  ingredient_index: number;
  new_ingredient_line: string;
};

export type GroceryCategory = {
  name?: string;
  items?: Array<{
    name?: string;
    quantity?: string | null;
  }>;
};

export type GroceryListResponse = {
  meal_plan_id: number;
  categories: GroceryCategory[];
};

export type SlotInstructionsResponse = {
  instructions?: string[];
  source_url?: string | null;
};

export type EmailMealPlanInput = {
  message?: string | null;
};

export type ClinicMealPlanSettings = {
  id: number;
  clinic_id: number;
  logo_storage_key?: string | null;
  brand_color_primary: string;
  brand_color_secondary: string;
  footer_text?: string | null;
};

export type ClinicMealPlanSettingsInput = {
  logo_storage_key?: string | null;
  brand_color_primary: string;
  brand_color_secondary: string;
  footer_text?: string | null;
};

/** Convenience alias — what the list/grid components consume. Same shape
 *  as the server's summary; kept as a separate exported name so call sites
 *  don't have to know "Summary" is the canonical type. */
export type MealPlan = MealPlanSummary;

/* -------------------------------------------------------------------------- */
/*                                 CLIENT                                     */
/* -------------------------------------------------------------------------- */

const treat404AsEmpty = async <T>(fn: () => Promise<T>, fallback: T): Promise<T> => {
  try {
    return await fn();
  } catch (err) {
    if ((err as { response?: { status?: number } }).response?.status === 404) return fallback;
    throw err;
  }
};

export const mealPlansApi = {
  /* -------------------------- LIST / GET / CRUD -------------------------- */

  async list(patientId: number): Promise<MealPlan[]> {
    return treat404AsEmpty(async () => {
      const res = await apiClient.get<MealPlanSummary[]>(`/patients/${patientId}/meal-plans`);
      return Array.isArray(res.data) ? res.data : [];
    }, []);
  },

  /**
   * Single-plan detail GET (`GET /patients/{id}/meal-plans/{plan_id}`).
   * Used by the viewer to load days/slots/grocery_list.
   */
  async get(patientId: number, planId: number): Promise<MealPlanDetail> {
    const res = await apiClient.get<MealPlanDetail>(
      `/patients/${patientId}/meal-plans/${planId}`,
    );
    return res.data;
  },

  async create(patientId: number, body: CreateMealPlanInput): Promise<MealPlanResponse> {
    const res = await apiClient.post<MealPlanResponse>(
      `/patients/${patientId}/meal-plans`,
      body,
    );
    return res.data;
  },

  async deletePlan(patientId: number, planId: number) {
    await apiClient.delete(`/patients/${patientId}/meal-plans/${planId}`);
  },

  /* ----------------------------- TDEE ----------------------------------- */

  /**
   * TDEE + BMR + suggested macro split. v1 debounces this 600ms while the
   * intake form fields settle — that lives in the hook layer (phase 2).
   * Endpoint is metric-only; converters in `lib/units.ts` (added in phase 2)
   * will handle imperial→metric.
   */
  async tdeeCalculate(patientId: number, body: TdeeCalculateInput): Promise<TdeeCalculateResponse> {
    const res = await apiClient.post<TdeeCalculateResponse>(
      `/patients/${patientId}/meal-plans/tdee-calculate`,
      body,
    );
    return res.data;
  },

  /* ------------------------ LIFECYCLE MUTATIONS ------------------------- */

  /**
   * Kick off AI generation. Returns 202 + a `TaskResultResponse` (a job
   * handle); the viewer then polls `get()` every 3s until status flips
   * out of `generating`.
   */
  async generate(patientId: number, planId: number): Promise<{ task_id?: string } & Record<string, unknown>> {
    const res = await apiClient.post(`/patients/${patientId}/meal-plans/${planId}/generate`);
    return (res.data ?? {}) as { task_id?: string } & Record<string, unknown>;
  },

  async finalize(patientId: number, planId: number): Promise<MealPlanDetail> {
    const res = await apiClient.post<MealPlanDetail>(
      `/patients/${patientId}/meal-plans/${planId}/finalize`,
    );
    return res.data;
  },

  async duplicate(patientId: number, planId: number): Promise<MealPlanResponse> {
    const res = await apiClient.post<MealPlanResponse>(
      `/patients/${patientId}/meal-plans/${planId}/duplicate`,
    );
    return res.data;
  },

  /* ------------------------------ SWAP ---------------------------------- */

  async swapSuggestions(
    patientId: number,
    planId: number,
    body: SwapSuggestionsInput,
  ): Promise<SwapSuggestionsResponse> {
    const res = await apiClient.post<SwapSuggestionsResponse>(
      `/patients/${patientId}/meal-plans/${planId}/swap-suggestions`,
      body,
    );
    return res.data;
  },

  async swapConfirm(
    patientId: number,
    planId: number,
    body: SwapConfirmInput,
  ): Promise<SwapRecipeResponse> {
    const res = await apiClient.post<SwapRecipeResponse>(
      `/patients/${patientId}/meal-plans/${planId}/swap-confirm`,
      body,
    );
    return res.data;
  },

  async swapIngredient(
    patientId: number,
    planId: number,
    body: SwapIngredientInput,
  ): Promise<SwapIngredientResponse> {
    const res = await apiClient.post<SwapIngredientResponse>(
      `/patients/${patientId}/meal-plans/${planId}/swap-ingredient`,
      body,
    );
    return res.data;
  },

  async confirmIngredientSwap(
    patientId: number,
    planId: number,
    body: ConfirmIngredientSwapInput,
  ): Promise<MealPlanSlot> {
    const res = await apiClient.post<MealPlanSlot>(
      `/patients/${patientId}/meal-plans/${planId}/confirm-ingredient-swap`,
      body,
    );
    return res.data;
  },

  /* ---------------------------- GROCERY --------------------------------- */

  async groceryList(patientId: number, planId: number): Promise<GroceryListResponse> {
    const res = await apiClient.post<GroceryListResponse>(
      `/patients/${patientId}/meal-plans/${planId}/grocery-list`,
    );
    return res.data;
  },

  /* ---------------------------- SLOT ----------------------------------- */

  /**
   * Lazy-fetched recipe instructions for a single slot. v1 only fetches
   * when the user expands the "View Instructions" panel and caches the
   * result on the card.
   */
  async slotInstructions(
    patientId: number,
    planId: number,
    slotId: number,
  ): Promise<SlotInstructionsResponse> {
    const res = await apiClient.get<SlotInstructionsResponse>(
      `/patients/${patientId}/meal-plans/${planId}/slots/${slotId}/instructions`,
    );
    return res.data;
  },

  /**
   * Save a clinician note on a slot. v1 surfaces this as a per-recipe
   * "Add Note" / "Edit Note" button; the note is stored on
   * `slot.recipe_data.clinician_notes`.
   */
  async saveSlotNote(
    patientId: number,
    planId: number,
    slotId: number,
    note: string,
  ): Promise<void> {
    await apiClient.post(
      `/patients/${patientId}/meal-plans/${planId}/slots/${slotId}/note`,
      { note },
    );
  },

  /* ----------------------------- PDF / EMAIL ---------------------------- */

  /**
   * Generate and download the meal-plan PDF. Server may return
   * `application/pdf` or, in some error states, `text/html` (an HTML
   * error page) — v1 detects via `content-type` and adjusts the
   * download filename. We return the raw `{blob, contentType}` so the
   * caller can do the same.
   */
  async pdf(
    patientId: number,
    planId: number,
  ): Promise<{ blob: Blob; contentType: string }> {
    const res = await apiClient.post(`/patients/${patientId}/meal-plans/${planId}/pdf`, undefined, {
      responseType: 'blob',
    });
    const contentType =
      (res.headers as Record<string, string | undefined>)['content-type'] ?? 'application/pdf';
    return { blob: res.data as Blob, contentType };
  },

  async email(patientId: number, planId: number, body: EmailMealPlanInput = {}): Promise<{ message?: string }> {
    const res = await apiClient.post<{ message?: string }>(
      `/patients/${patientId}/meal-plans/${planId}/email`,
      body,
    );
    return res.data ?? {};
  },

  /* ---------------------- CLINIC MEAL-PLAN SETTINGS --------------------- */

  /**
   * Per-clinic branding for generated PDFs / emails. Read by both the
   * viewer (preview) and (eventually) a clinic-admin settings page —
   * surfaced here for completeness so phase 2 doesn't have to re-touch
   * the API layer.
   */
  async clinicSettings(clinicId: number): Promise<ClinicMealPlanSettings | null> {
    return treat404AsEmpty(async () => {
      const res = await apiClient.get<ClinicMealPlanSettings>(
        `/clinics/${clinicId}/meal-plan-settings`,
      );
      return res.data;
    }, null);
  },

  async updateClinicSettings(
    clinicId: number,
    body: ClinicMealPlanSettingsInput,
  ): Promise<ClinicMealPlanSettings> {
    const res = await apiClient.put<ClinicMealPlanSettings>(
      `/clinics/${clinicId}/meal-plan-settings`,
      body,
    );
    return res.data;
  },
};
